import { Fragment } from 'react';
import { Controller, useFieldArray } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { getPlatformConfig } from '@/shared/constants';
import { OptimizedImage } from '@/shared/ui';
import { ChannelsIcon } from '@/shared/ui/Icons';

import { FieldLabel, FormSection, PersonaTextField } from '../../PersonaFormSections';
import { useEditPersonaFormContext } from '../hooks/useEditPersonaFormContext';

/** Mirrors the 1 / 2.75 / 1.25 / 7 split of the twelve-column layout it replaces. */
const CHANNEL_ROW_COLUMNS = 'grid grid-cols-[1fr_2.75fr_1.25fr_7fr] items-center gap-4';

export function ChannelsSection() {
  const { t } = useTranslation('audiences');

  const { control } = useEditPersonaFormContext();
  const { fields } = useFieldArray({
    control,
    name: 'channels',
  });

  return (
    <FormSection icon={<ChannelsIcon />} title={t('audiences.channelsSection.channels')}>
      <div className={CHANNEL_ROW_COLUMNS}>
        <span />
        <FieldLabel>{t('audiences.channelsSection.channel')}</FieldLabel>
        <FieldLabel>{t('audiences.channelsSection.share')}</FieldLabel>
        <FieldLabel required>{t('audiences.channelsSection.description')}</FieldLabel>

        {fields.map((channel, index) => {
          const hasShare = channel.sharePercent > 0;
          const platformImage = getPlatformConfig(channel.platformName)?.image;

          return (
            <Fragment key={channel.id}>
              <div className="flex justify-center">
                {platformImage && (
                  <OptimizedImage
                    src={platformImage}
                    alt={channel.platformName}
                    className="size-6"
                  />
                )}
              </div>
              <p className="text-sm">{channel.platformName}</p>
              <p className="text-sm">{channel.sharePercent}</p>
              {hasShare ? (
                <Controller
                  name={`channels.${index}.description`}
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <PersonaTextField
                      {...field}
                      aria-label={channel.platformName}
                      placeholder={t('audiences.channelsSection.description')}
                      error={error?.message}
                    />
                  )}
                />
              ) : (
                <p className="text-sm text-muted-foreground">—</p>
              )}
            </Fragment>
          );
        })}
      </div>
    </FormSection>
  );
}
